import moment from 'moment';

import { MeteringMethodModel } from '../metering-method.model';
import { ContractOfferType } from '../types/contract-offer-type';
import { GridConnection } from '../types/grid-connection.enum';

import { ContractEngagement } from './contract-engagement.model';
import { ContractPriceStrip } from './contract-price-strip.model';
import { Contract } from './contract.model';
import { DeliveryPoint } from './delivery-point.model';
import { Site } from './site.model';

/**
 * Model for API ContractPerimeter Model
 *
 * Properties for patch perimeter are set manualy in contract-editor-aggregate.
 * If one person want the property is patched with 'perimeter header' (simple column in perimeter db),
 * make sure to add it in headerPerimeterEqualTo() below and add it in manualy in contract-editor-aggregate.service.
 * Otherwise, make sure to call the appropriate endpoint for the property.
 */
export class ContractPerimeter {
  /**
   * Perimeter unique id.
   */
  id: number;

  /**
   * Name.
   */
  name: string;

  /**
   * Engagement
   */
  engagement: ContractEngagement;

  /**
   * Start date.
   */
  startDate: moment.Moment;

  /**
   * End date.
   */
  endDate: moment.Moment;

  /**
   * Perimeter’s points.
   */
  points: DeliveryPoint[];

  /**
   * Price strips
   */
  priceStrips: ContractPriceStrip[];

  /**
   * Gemforce Opportunity Id.
   */
  gemforceOpportunityId: string;

  offerType: ContractOfferType;

  minDate: moment.Moment;
  maxDate: moment.Moment;
  durations: number[];
  duration: number;
  sitesGroupsFilterQuery = '';
  gridConnection: GridConnection;

  constructor(attributes: Partial<ContractPerimeter> = {}) {
    Object.assign(this, attributes);
  }

  assignSitesToPoints(sites: Site[]) {
    this.points.forEach((p) => {
      const site = p.getSite(sites);
      if (site) {
        p.site = site;
        p.siteLabels = site.labels;
      }
    });
  }

  assignContractToPoints(contract: Contract) {
    this.points.forEach((p) => (p.contract = contract));
  }

  assignMeteringMethodToPoints(meteringMethods: MeteringMethodModel[] = []) {
    this.points.forEach((p) => (p.meteringMethod = p.getMeteringMethod(meteringMethods)));
  }

  get sortedPoints() {
    return this.points
      .sort((a, b) => a.meteringPoint?.localeCompare(b.meteringPoint))
      .sort((a, b) => a.siteLabels?.external?.localeCompare(b.siteLabels?.external));
  }

  sortPoints(sites: Site[], contract: Contract, meteringMethods: MeteringMethodModel[] = []) {
    if (sites !== undefined && contract !== undefined) {
      this.assignContractToPoints(contract);
      this.assignSitesToPoints(sites);
      if (meteringMethods) this.assignMeteringMethodToPoints(meteringMethods);
      return this.sortedPoints;
    }
    return this.points;
  }

  headerPerimeterEqualTo(otherPerimeter: ContractPerimeter): boolean {
    return (
      this.name === otherPerimeter.name &&
      this.startDate.isSame(otherPerimeter.startDate) &&
      this.endDate.isSame(otherPerimeter.endDate) &&
      this.gridConnection === otherPerimeter.gridConnection &&
      this.offerType === otherPerimeter.offerType &&
      this.gemforceOpportunityId === otherPerimeter.gemforceOpportunityId
    );
  }
}
